import type {
  AdminUserDto,
  AuthResultDto,
  BedDto,
  BedSummaryDto,
  CreateBedInput,
  CreateGuestInput,
  CreateHouseInput,
  CreatePlanLockInput,
  CreateRoomInput,
  CreateSojournInput,
  GeneratePlanInput,
  GuestDto,
  HouseDto,
  HouseholdDto,
  ImpersonateInput,
  LoginInput,
  MergeHouseholdsInput,
  MergeHouseholdsResultDto,
  PlanDto,
  PlanLockDto,
  RegisterInput,
  RenameHouseholdInput,
  RoomDto,
  RoomSummaryDto,
  SetSojournDayNoteInput,
  SetSojournGuestInput,
  SetSojournHouseInput,
  SessionDto,
  SetPasswordInput,
  SojournDetailDto,
  SojournDto,
  UpdateBedInput,
  UpdateGuestInput,
  UpdateHouseInput,
  UpdateRoomInput,
  UpdateSojournInput,
} from '@room-mates/shared';
import type { HttpClient } from './httpClient.js';

export interface HouseWithRoomsDto extends HouseDto {
  readonly rooms: readonly RoomDto[];
}

/**
 * The API as a typed vocabulary.
 *
 * Every path lives here exactly once and every payload is typed from the shared
 * contracts, so a change to the server's schema is a compile error in the
 * client rather than a runtime surprise.
 */
export function createApi(http: HttpClient) {
  return {
    auth: {
      register: (input: RegisterInput) => http.post<AuthResultDto>('/auth/register', input),
      login: (input: LoginInput) => http.post<AuthResultDto>('/auth/login', input),
      me: () => http.get<SessionDto>('/auth/me'),
    },

    houses: {
      list: () => http.get<HouseDto[]>('/houses'),
      get: (houseId: string) => http.get<HouseWithRoomsDto>(`/houses/${houseId}`),
      create: (input: CreateHouseInput) => http.post<HouseDto>('/houses', input),
      update: (houseId: string, input: UpdateHouseInput) =>
        http.patch<HouseDto>(`/houses/${houseId}`, input),
      remove: (houseId: string) => http.delete(`/houses/${houseId}`),
    },

    rooms: {
      /** Every room the user owns, across all houses. */
      listAll: () => http.get<RoomSummaryDto[]>('/rooms'),
      list: (houseId: string) => http.get<RoomDto[]>(`/houses/${houseId}/rooms`),
      create: (houseId: string, input: CreateRoomInput) =>
        http.post<RoomDto>(`/houses/${houseId}/rooms`, input),
      update: (houseId: string, roomId: string, input: UpdateRoomInput) =>
        http.patch<RoomDto>(`/houses/${houseId}/rooms/${roomId}`, input),
      remove: (houseId: string, roomId: string) =>
        http.delete(`/houses/${houseId}/rooms/${roomId}`),
    },

    beds: {
      /** Every bed the user owns, across all houses and rooms. */
      listAll: () => http.get<BedSummaryDto[]>('/beds'),
      list: (houseId: string, roomId: string) =>
        http.get<BedDto[]>(`/houses/${houseId}/rooms/${roomId}/beds`),
      create: (houseId: string, roomId: string, input: CreateBedInput) =>
        http.post<BedDto>(`/houses/${houseId}/rooms/${roomId}/beds`, input),
      update: (houseId: string, roomId: string, bedId: string, input: UpdateBedInput) =>
        http.patch<BedDto>(`/houses/${houseId}/rooms/${roomId}/beds/${bedId}`, input),
      remove: (houseId: string, roomId: string, bedId: string) =>
        http.delete(`/houses/${houseId}/rooms/${roomId}/beds/${bedId}`),
    },

    guests: {
      list: () => http.get<GuestDto[]>('/guests'),
      get: (guestId: string) => http.get<GuestDto>(`/guests/${guestId}`),
      create: (input: CreateGuestInput) => http.post<GuestDto>('/guests', input),
      update: (guestId: string, input: UpdateGuestInput) =>
        http.patch<GuestDto>(`/guests/${guestId}`, input),
      remove: (guestId: string) => http.delete(`/guests/${guestId}`),
    },

    sojourns: {
      list: () => http.get<SojournDto[]>('/sojourns'),
      get: (sojournId: string) => http.get<SojournDetailDto>(`/sojourns/${sojournId}`),
      create: (input: CreateSojournInput) => http.post<SojournDto>('/sojourns', input),
      update: (sojournId: string, input: UpdateSojournInput) =>
        http.patch<SojournDto>(`/sojourns/${sojournId}`, input),
      remove: (sojournId: string) => http.delete(`/sojourns/${sojournId}`),

      setHouse: (sojournId: string, houseId: string, input: SetSojournHouseInput) =>
        http.put<SojournDetailDto>(`/sojourns/${sojournId}/houses/${houseId}`, input),
      removeHouse: (sojournId: string, houseId: string) =>
        http.delete(`/sojourns/${sojournId}/houses/${houseId}`),

      setGuest: (sojournId: string, guestId: string, input: SetSojournGuestInput) =>
        http.put<SojournDetailDto>(`/sojourns/${sojournId}/guests/${guestId}`, input),
      removeGuest: (sojournId: string, guestId: string) =>
        http.delete(`/sojourns/${sojournId}/guests/${guestId}`),

      setDayNote: (sojournId: string, date: string, input: SetSojournDayNoteInput) =>
        http.put<SojournDetailDto>(`/sojourns/${sojournId}/notes/${date}`, input),
    },

    locks: {
      list: (sojournId: string) => http.get<PlanLockDto[]>(`/sojourns/${sojournId}/locks`),
      create: (sojournId: string, input: CreatePlanLockInput) =>
        http.post<PlanLockDto>(`/sojourns/${sojournId}/locks`, input),
      remove: (sojournId: string, lockId: string) =>
        http.delete(`/sojourns/${sojournId}/locks/${lockId}`),
    },

    admin: {
      users: () => http.get<AdminUserDto[]>('/admin/users'),
      households: () => http.get<HouseholdDto[]>('/admin/households'),
      setPassword: (userId: string, input: SetPasswordInput) =>
        http.put<void>(`/admin/users/${userId}/password`, input),
      /** Gives the account a household to itself, leaving shared data behind. */
      detachHousehold: (userId: string) => http.post<void>(`/admin/users/${userId}/household`),
      renameHousehold: (householdId: string, input: RenameHouseholdInput) =>
        http.put<void>(`/admin/households/${householdId}`, input),
      mergeHouseholds: (householdId: string, input: MergeHouseholdsInput) =>
        http.post<MergeHouseholdsResultDto>(`/admin/households/${householdId}/merge`, input),
      impersonate: (input: ImpersonateInput) =>
        http.post<AuthResultDto>('/admin/impersonations', input),
    },

    plans: {
      get: (sojournId: string) => http.get<PlanDto>(`/sojourns/${sojournId}/plan`),
      generate: (sojournId: string, input: GeneratePlanInput = {}) =>
        http.post<PlanDto>(`/sojourns/${sojournId}/plan`, input),
      remove: (sojournId: string) => http.delete(`/sojourns/${sojournId}/plan`),
    },
  };
}

export type Api = ReturnType<typeof createApi>;
